import { ThemedText } from '@/components/themed-text';
import {
  Field,
  PillList,
  SectionCard,
  SectionDivider,
  StatRow,
  StatTile,
} from '@/components/ui/section-card';
import { paymentMethodLabel } from '@/constants/service-payment-methods';
import { PRICE_DISPLAY_MODE } from '@/types/price-display-mode';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { RECURRING_BOOKING_MODE } from '@/types/recurring-booking-mode';
import { Service } from '@/types/service';
import { formatDuration } from '@/utils/format-appointment';
import { formatCurrency } from '@/utils/format-currency';
import { formatQuota } from '@/utils/format-quota';
import React from 'react';
import { View } from 'react-native';

const PRICE_DISPLAY_HINT: Record<PRICE_DISPLAY_MODE, string> = {
  [PRICE_DISPLAY_MODE.SHOW]: 'Na sua página pública aparece o valor exato.',
  [PRICE_DISPLAY_MODE.FROM]: 'Na sua página pública aparece como "a partir de".',
  [PRICE_DISPLAY_MODE.HIDDEN]: 'Na sua página pública aparece como "sob consulta".',
};

const RECURRING_MODE_LABEL: Record<RECURRING_BOOKING_MODE, string> = {
  [RECURRING_BOOKING_MODE.OFF]: 'Não aceita',
  [RECURRING_BOOKING_MODE.AUTO]: 'O cliente monta',
  [RECURRING_BOOKING_MODE.REQUEST]: 'Sob conversa',
};

const RECURRING_MODE_HINT: Record<RECURRING_BOOKING_MODE, string> = {
  [RECURRING_BOOKING_MODE.OFF]: 'O cliente marca uma data por vez neste serviço.',
  [RECURRING_BOOKING_MODE.AUTO]:
    'O cliente monta a agenda dele dentro da sua grade e você aprova.',
  [RECURRING_BOOKING_MODE.REQUEST]:
    'O cliente propõe a recorrência numa conversa e só vale depois que você aceita.',
};

const BILLING_INTERVAL_LABEL: Record<string, string> = {
  WEEKLY: 'Semanal',
  MONTHLY: 'Mensal',
  BIMONTHLY: 'Bimestral',
  TRIMESTERLY: 'Trimestral',
  SEMIANNUALLY: 'Semestral',
  YEARLY: 'Anual',
};

/**
 * Leitura da configuração do serviço, nos mesmos blocos do formulário: preço,
 * agendamento, local e descrição. Editar é a mesma tela em outro modo.
 */
export function ServiceDetailsTab({ service }: { service: Service }) {
  const methods = service.allowedPaymentMethods ?? [];
  const schedulable = !!service.durationMinutes;
  const recurringMode = service.recurringBookingMode ?? RECURRING_BOOKING_MODE.OFF;
  const subscription = !!service.isSubscription;
  // Só o avulso que aceita repetição escolhe cadência e validade: assinatura é
  // sempre mensal e vale até alguém cancelar.
  const recurring = !subscription && recurringMode !== RECURRING_BOOKING_MODE.OFF;
  const capacity = service.slotCapacity ?? 1;
  const priceMode = service.priceDisplayMode ?? PRICE_DISPLAY_MODE.SHOW;
  const hasPrice = service.defaultPrice != null;

  return (
    <View className="w-full gap-4 px-5 pt-5">
      <SectionCard icon="pricetag-outline" title="Preço e pagamento">
        <View className="gap-0.5">
          <ThemedText className="text-2xl font-bold text-neutral-900">
            {hasPrice ? formatCurrency(service.defaultPrice as number) : 'Preço a combinar'}
          </ThemedText>
          <ThemedText className="text-xs leading-4 text-neutral-500">
            {hasPrice
              ? PRICE_DISPLAY_HINT[priceMode]
              : 'O valor é acertado em cada contrato, sem preço fixo na página pública.'}
          </ThemedText>
        </View>

        <SectionDivider />

        <PillList label="Formas de pagamento" items={methods.map(paymentMethodLabel)} />
      </SectionCard>

      <SectionCard
        icon="calendar-outline"
        title="Agendamento online"
        badge={schedulable ? 'Agendável' : 'Não agendável'}
        badgeTone={schedulable ? 'success' : 'muted'}
      >
        {schedulable ? (
          <StatRow>
            <StatTile
              label="Duração"
              value={formatDuration(service.durationMinutes as number)}
            />
            <StatTile
              label="Vagas por horário"
              value={capacity > 1 ? `Até ${capacity}` : 'Individual'}
            />
          </StatRow>
        ) : (
          <ThemedText className="text-sm leading-5 text-neutral-500">
            Sem duração definida, este serviço não vira horário na agenda — ele só entra em
            contrato.
          </ThemedText>
        )}

        <SectionDivider />

        {subscription ? (
          <>
            <Field
              label="Como o cliente contrata"
              value="Assinatura"
              hint="O cliente assina, paga todo mês e marca as sessões dentro da cota — pode deixar sempre no mesmo dia e horário, ou marcar aos poucos."
            />
            <StatRow>
              <StatTile
                label="Sessões"
                value={formatQuota(
                  service.subscriptionQuota ?? 1,
                  service.subscriptionQuotaPeriod ?? QUOTA_PERIOD.MONTH,
                )}
              />
              <StatTile label="Cobrança" value="Mensal" />
            </StatRow>
          </>
        ) : (
          <Field
            label="Sessões repetidas"
            value={RECURRING_MODE_LABEL[recurringMode]}
            hint={RECURRING_MODE_HINT[recurringMode]}
          />
        )}

        {recurring && (
          <StatRow>
            <StatTile
              label="Cobrança"
              value={
                BILLING_INTERVAL_LABEL[service.recurringBillingInterval ?? 'MONTHLY'] ??
                'Recorrente'
              }
            />
            <StatTile
              label="Validade"
              value={`${service.recurringHorizonDays ?? 90} dias`}
            />
          </StatRow>
        )}
      </SectionCard>

      <SectionCard icon="location-outline" title="Onde acontece">
        {service.address ? (
          <Field label="Endereço fixo" value={service.address} />
        ) : (
          <ThemedText className="text-sm leading-5 text-neutral-500">
            Sem endereço fixo — o local é combinado em cada contrato.
          </ThemedText>
        )}
      </SectionCard>

      {service.description ? (
        <SectionCard icon="document-text-outline" title="Descrição">
          <ThemedText selectable className="text-sm leading-6 text-neutral-700">
            {service.description}
          </ThemedText>
        </SectionCard>
      ) : null}
    </View>
  );
}
